import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import { MemoryRouter, Route, Routes, useLocation } from "react-router-dom";
import { ActionButtonElement } from "./ActionButtonElement";

const mockAuthFetch = jest.fn();
const mockRuntime = {
  workspaceId: "workspace-1",
  canvasId: "source-canvas",
  rowId: "own-row",
  rowData: { id: "own-row" },
  embeddingPath: undefined as
    Array<{ canvasId: string; elementId: string }> | undefined,
  interactionBus: { getSelectedRow: jest.fn() },
};

jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => mockRuntime,
}));

const target = "11111111-1111-4111-8111-111111111111";

function PathProbe() {
  const location = useLocation();
  return <span data-testid="path">{location.pathname + location.search}</span>;
}

function renderButton(action: object, readOnly = false) {
  return render(
    <MemoryRouter
      initialEntries={["/workspace/workspace-1/myvault/canvases/source-canvas"]}
    >
      <Routes>
        <Route
          path="/workspace/:workspaceId/myvault/canvases/:id"
          element={
            <>
              <ActionButtonElement
                props={{ label: "Open chart", direct_action: action }}
                isPreview={false}
                readOnly={readOnly}
                elementId="button-1"
              />
              <PathProbe />
            </>
          }
        />
        <Route
          path="/workspace/:workspaceId/myvault/canvases/:id/destination"
          element={<span>destination</span>}
        />
        <Route
          path="/workspace/:workspaceId/myvault/views/:id"
          element={<PathProbe />}
        />
      </Routes>
    </MemoryRouter>,
  );
}

describe("ActionButtonElement navigation", () => {
  beforeEach(() => {
    mockAuthFetch.mockReset();
    mockRuntime.embeddingPath = undefined;
  });

  it("disables a mutating action at read access", () => {
    renderButton({ type: "SEND_EMAIL", config: {} }, true);
    expect(screen.getByRole("button", { name: "Open chart" })).toBeDisabled();
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    expect(mockAuthFetch).not.toHaveBeenCalled();
  });

  it("posts mutating actions through the canvas and element context", async () => {
    mockAuthFetch.mockResolvedValue({ success: true, message: "Sent" });
    renderButton({ type: "SEND_EMAIL", config: {} });
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/canvases/source-canvas/trigger-action",
        "trigger action button",
        expect.objectContaining({
          method: "POST",
          body: JSON.stringify({
            element_id: "button-1",
            row_id: "own-row",
            row_data: { id: "own-row" },
          }),
        }),
      ),
    );
  });

  it("includes the ordered host path for an embedded action", async () => {
    mockRuntime.embeddingPath = [
      { canvasId: "outer", elementId: "host-a" },
      { canvasId: "middle", elementId: "host-b" },
    ];
    mockAuthFetch.mockResolvedValue({ success: true });
    renderButton({ type: "SEND_EMAIL", config: {} });
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/myvault/canvases/source-canvas/trigger-action?embedding_path=outer%3Ahost-a&embedding_path=middle%3Ahost-b",
        "trigger action button",
        expect.objectContaining({ method: "POST" }),
      ),
    );
  });

  it("keeps a navigation action available at read access", async () => {
    mockAuthFetch.mockResolvedValue({ id: target });
    renderButton(
      {
        type: "NAVIGATE",
        config: { target_canvas_id: target, record_source: "none" },
      },
      true,
    );
    expect(screen.getByRole("button", { name: "Open chart" })).toBeEnabled();
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    await waitFor(() =>
      expect(screen.getByTestId("path")).toHaveTextContent(
        `/workspace/workspace-1/myvault/canvases/${target}`,
      ),
    );
  });

  it("opens the target with current row context without posting an action", async () => {
    mockAuthFetch.mockResolvedValue({ id: target });
    renderButton({
      type: "NAVIGATE",
      config: { target_canvas_id: target, record_source: "current" },
    });
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        `/api/workspace/myvault/canvases/${target}/full`,
        expect.any(String),
        expect.any(Object),
      ),
    );
    await waitFor(() =>
      expect(screen.getByTestId("path")).toHaveTextContent(
        `/workspace/workspace-1/myvault/canvases/${target}?row=own-row`,
      ),
    );
    expect(mockAuthFetch).not.toHaveBeenCalledWith(
      expect.stringContaining("trigger-action"),
      expect.anything(),
      expect.anything(),
    );
  });

  it("keeps an unavailable destination on the source canvas", async () => {
    mockAuthFetch.mockRejectedValue(new Error("Access denied"));
    renderButton({
      type: "NAVIGATE",
      config: { target_canvas_id: target, record_source: "none" },
    });
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    expect(
      await screen.findByText(/destination unavailable/i),
    ).toBeInTheDocument();
    expect(screen.getByTestId("path")).toHaveTextContent(
      "/workspace/workspace-1/myvault/canvases/source-canvas",
    );
  });

  it("opens an authorized view without a record when configured", async () => {
    mockAuthFetch.mockResolvedValue({ id: target });
    renderButton({
      type: "NAVIGATE",
      config: { target_view_id: target, record_source: "none" },
    });
    fireEvent.click(screen.getByRole("button", { name: "Open chart" }));
    await waitFor(() =>
      expect(screen.getByTestId("path")).toHaveTextContent(
        `/workspace/workspace-1/myvault/views/${target}`,
      ),
    );
    expect(mockAuthFetch).toHaveBeenCalledWith(
      `/api/workspace/myvault/views/${target}`,
      expect.any(String),
      expect.any(Object),
    );
  });
});
